import { gql } from '@apollo/client'
import { DoctrineMetadata, JurisprudenceMetadata } from '@jusbrasil-web/shared-apollo-client/types'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { cloneElement, isValidElement, ReactNode, useEffect, useRef } from 'react'

import { CopyRequireLoginTriggerUtil } from '../../functions'
import { CopyContentTriggerStandalone } from '../copy-content-trigger'
import {
  CopyRequireLoginTrigger_DocumentFragment as Document,
  CopyRequireLoginTrigger_HaystackItemFragment as HaystackItem,
  CopyRequireLoginTrigger_ViewerFragment,
} from './__generated__/copy-require-login-trigger.graphql'

let copyRequireLoginTriggerUtil: CopyRequireLoginTriggerUtil | null = null

export interface CopyRequireLoginTriggerProps {
  appId: string
  className?: string
  children: ReactNode
  documentOrHaystackItem: Document | HaystackItem
  featureCode: string
  ssrEventsProperties?: Record<string, unknown>
  viewer: CopyRequireLoginTrigger_ViewerFragment | null
  ga4EventData?: {
    componentClass: string
    componentName?: string
    copyFeatureName?: string
  }
}

function isDocument(
  documentOrHaystackItem: Document | HaystackItem
): documentOrHaystackItem is Document {
  if ((documentOrHaystackItem as Document).docId) {
    return true
  }
  return false
}

function getDocumentData(documentOrHaystackItem: Document | HaystackItem) {
  if (isDocument(documentOrHaystackItem)) {
    const document = documentOrHaystackItem as Document
    return { kind: document.artifact, docId: document.docId }
  }
  const { kind, data } = documentOrHaystackItem as HaystackItem
  const { docId } = data as JurisprudenceMetadata | DoctrineMetadata
  return { kind: kind, docId }
}

export function CopyRequireLoginTrigger(props: CopyRequireLoginTriggerProps) {
  const {
    appId,
    className,
    featureCode,
    ssrEventsProperties,
    viewer,
    documentOrHaystackItem,
    ga4EventData,
  } = props

  const elementRef = useRef(null)
  const { kind, docId } = getDocumentData(documentOrHaystackItem)
  const { sendGA4Event } = useGA4Dispatcher()

  useEffect(() => {
    if (!viewer && elementRef.current) {
      copyRequireLoginTriggerUtil = new CopyRequireLoginTriggerUtil({
        document: { artifact: kind, docId },
        element: elementRef.current,
        options: {
          eventData: {
            ...ssrEventsProperties,
            app_id: appId,
            feature_code: featureCode,
          },
        },
        sendGA4Event,
        ga4EventData,
      })
      copyRequireLoginTriggerUtil.addCopyListener()
    }

    return () => {
      if (copyRequireLoginTriggerUtil) {
        copyRequireLoginTriggerUtil.removeCopyListener()
      }
    }
  }, [appId, kind, docId, featureCode, ssrEventsProperties, viewer, ga4EventData, sendGA4Event])

  return (
    <div className={className} ref={elementRef}>
      {renderChildren(props)}
    </div>
  )
}

function renderChildren(props: CopyRequireLoginTriggerProps) {
  const { children, viewer } = props
  if (
    isValidElement<{ disabled?: boolean }>(children) &&
    children.type === CopyContentTriggerStandalone &&
    !viewer?.pid // unlogged
  ) {
    return cloneElement(children, { disabled: true })
  }
  return children
}

CopyRequireLoginTrigger.fragments = {
  document: gql`
    fragment CopyRequireLoginTrigger_document on Document {
      artifact
      docId
    }
  `,

  haystackItem: gql`
    fragment CopyRequireLoginTrigger_haystackItem on HaystackItem {
      kind
      data {
        ... on JurisprudenceMetadata {
          docId
        }

        ... on DoctrineMetadata {
          docId
        }
      }
    }
  `,

  viewer: gql`
    fragment CopyRequireLoginTrigger_viewer on Profile {
      pid
    }
  `,
}
